import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchApi } from 'core/utils/api';
import { REQUEST_TYPE_ACCESS, REQUEST_TYPE_GUARDING, REQUEST_TYPE_VISITOR } from 'core/utils/value';

import DateSelectionForm from 'pages/VisitorRequestForm/DateSelectionForm';
import VisitorSelectionForm from 'pages/VisitorRequestForm/VisitorSelectionForm';
import SiteAccessForm from 'pages/VisitorRequestForm/SiteAccessForm';
import WorkTypeSelectionForm from 'pages/VisitorRequestForm/WorkTypeSelectionForm';
import ReviewForm from 'pages/VisitorRequestForm/ReviewForm';

import GuardingJobDetailsForm from 'pages/GuardingRequestForm/JobDetailsForm';
import GuardingDateSelectionForm from 'pages/GuardingRequestForm/DateSelectionForm';
import GuardingVisitorSelectionForm from 'pages/GuardingRequestForm/VisitorSelectionForm';
import GuardingReviewForm from 'pages/GuardingRequestForm/ReviewForm';

import AccessJobDetailsForm from 'pages/AccessRequestForm/JobDetailsForm';
import AccessDateSelectionForm from 'pages/AccessRequestForm/DateSelectionForm';
import AccessVisitorSelectionForm from 'pages/AccessRequestForm/VisitorSelectionForm';
import AccessSiteAccessForm from 'pages/AccessRequestForm/SiteAccessForm';
import AccessReviewForm from 'pages/AccessRequestForm/ReviewForm';

import './guestBooking.scss';

/**
 * Serves the right step screen for a guest booking link.
 *
 * A link can raise a visit, general access or guarding request, and route step
 * numbers mean different things in each chain (step 4 is Site Access on a visit
 * request, Access Type on an access request and Review on a guarding one). The
 * guest has no request id to route on, so the type is read from the token's own
 * payload and the screen chosen from that.
 */
const STEP_SCREENS: Record<string, Record<number, React.ComponentType>> = {
    [REQUEST_TYPE_GUARDING]: {
        1: GuardingJobDetailsForm,
        2: GuardingDateSelectionForm,
        3: GuardingVisitorSelectionForm,
        4: GuardingReviewForm,
    },
    [REQUEST_TYPE_ACCESS]: {
        1: AccessJobDetailsForm,
        2: AccessDateSelectionForm,
        3: AccessVisitorSelectionForm,
        4: AccessSiteAccessForm,
        5: AccessReviewForm,
    },
    [REQUEST_TYPE_VISITOR]: {
        2: DateSelectionForm,
        3: VisitorSelectionForm,
        4: SiteAccessForm,
        5: WorkTypeSelectionForm,
        6: ReviewForm,
    },
};

const GuestStepRouter: React.FC = () => {
    const { token, step } = useParams<{ token: string, step: string }>();
    const [requestType, setRequestType] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;

        const load = async () => {
            try {
                const response = await fetchApi({ method: 'GET', url: `booking/${token}` });
                if (active) {
                    setRequestType(response.data?.requestType || REQUEST_TYPE_VISITOR);
                }
            } catch (e) {
                if (active) {
                    setError('This link is invalid or has expired.');
                }
            }
        };

        load();

        return () => { active = false; };
    }, [token]);

    if (error || (requestType && !STEP_SCREENS[requestType])) {
        return (
            <div className="guest-booking">
                <p className="guest-booking__message">{error || 'This booking could not be opened.'}</p>
            </div>
        );
    }

    if (!requestType) {
        return (
            <div className="guest-booking">
                <p className="guest-booking__message">Loading your booking…</p>
            </div>
        );
    }

    const Screen = STEP_SCREENS[requestType]?.[Number(step)];

    if (!Screen) {
        return (
            <div className="guest-booking">
                <p className="guest-booking__message">That step is not part of this booking.</p>
            </div>
        );
    }

    return <Screen />;
};

export default GuestStepRouter;
